// Internationalization support
type Locale = 'en' | 'es' | 'fr' | 'de' | 'ja' | 'zh';

interface Translations {
  [key: string]: string | Translations;
}

const translations: Record<Locale, Translations> = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
    },
    media: {
      title: 'Media Library',
      upload: 'Upload Media',
      play: 'Play',
      pause: 'Pause',
      fullscreen: 'Fullscreen',
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signOut: 'Sign Out',
      email: 'Email',
      password: 'Password',
    },
  },
  es: {
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      create: 'Crear',
      search: 'Buscar',
      filter: 'Filtrar',
      sort: 'Ordenar',
    },
    media: {
      title: 'Biblioteca de Medios',
      upload: 'Subir Medios',
      play: 'Reproducir',
      pause: 'Pausar',
      fullscreen: 'Pantalla Completa',
    },
    auth: {
      signIn: 'Iniciar Sesión',
      signUp: 'Registrarse',
      signOut: 'Cerrar Sesión',
      email: 'Correo Electrónico',
      password: 'Contraseña',
    },
  },
  fr: {
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      search: 'Rechercher',
      filter: 'Filtrer',
      sort: 'Trier',
    },
    media: {
      title: 'Bibliothèque Multimédia',
      upload: 'Télécharger des Médias',
      play: 'Lire',
      pause: 'Pause',
      fullscreen: 'Plein Écran',
    },
    auth: {
      signIn: 'Se Connecter',
      signUp: "S'inscrire",
      signOut: 'Se Déconnecter',
      email: 'E-mail',
      password: 'Mot de Passe',
    },
  },
  de: {
    common: {
      loading: 'Laden...',
      error: 'Fehler',
      success: 'Erfolg',
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      create: 'Erstellen',
      search: 'Suchen',
      filter: 'Filtern',
      sort: 'Sortieren',
    },
    media: {
      title: 'Medienbibliothek',
      upload: 'Medien Hochladen',
      play: 'Abspielen',
      pause: 'Pause',
      fullscreen: 'Vollbild',
    },
    auth: {
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      signOut: 'Abmelden',
      email: 'E-Mail',
      password: 'Passwort',
    },
  },
  ja: {
    common: {
      loading: '読み込み中...',
      error: 'エラー',
      success: '成功',
      save: '保存',
      cancel: 'キャンセル',
      delete: '削除',
      edit: '編集',
      create: '作成',
      search: '検索',
      filter: 'フィルター',
      sort: '並べ替え',
    },
    media: {
      title: 'メディアライブラリ',
      upload: 'メディアをアップロード',
      play: '再生',
      pause: '一時停止',
      fullscreen: '全画面表示',
    },
    auth: {
      signIn: 'ログイン',
      signUp: '新規登録',
      signOut: 'ログアウト',
      email: 'メールアドレス',
      password: 'パスワード',
    },
  },
  zh: {
    common: {
      loading: '加载中...',
      error: '错误',
      success: '成功',
      save: '保存',
      cancel: '取消',
      delete: '删除',
      edit: '编辑',
      create: '创建',
      search: '搜索',
      filter: '过滤',
      sort: '排序',
    },
    media: {
      title: '媒体库',
      upload: '上传媒体',
      play: '播放',
      pause: '暂停',
      fullscreen: '全屏',
    },
    auth: {
      signIn: '登录',
      signUp: '注册',
      signOut: '登出',
      email: '邮箱',
      password: '密码',
    },
    cookies: {
      title: 'Cookie设置',
      description: '我们使用Cookie来改善您的体验。',
      accept: '接受',
      decline: '拒绝',
      learnMore: '了解更多',
    },
  },
};

// Add cookie translations to other languages
translations.en = {
  ...translations.en,
  cookies: {
    title: 'Cookie Settings',
    description: 'We use cookies to improve your experience.',
    accept: 'Accept',
    decline: 'Decline',
    learnMore: 'Learn More',
  },
};

translations.es = {
  ...translations.es,
  cookies: {
    title: 'Configuración de Cookies',
    description: 'Usamos cookies para mejorar su experiencia.',
    accept: 'Aceptar',
    decline: 'Rechazar',
    learnMore: 'Más Información',
  },
};

translations.fr = {
  ...translations.fr,
  cookies: {
    title: 'Paramètres des Cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience.',
    accept: 'Accepter',
    decline: 'Refuser',
    learnMore: 'En Savoir Plus',
  },
};

translations.de = {
  ...translations.de,
  cookies: {
    title: 'Cookie-Einstellungen',
    description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern.',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',
    learnMore: 'Mehr Erfahren',
  },
};

translations.ja = {
  ...translations.ja,
  cookies: {
    title: 'Cookie設定',
    description: 'エクスペリエンスを向上させるためにCookieを使用します。',
    accept: '同意する',
    decline: '拒否する',
    learnMore: '詳細を見る',
  },
};

class I18n {
  private currentLocale: Locale = 'en';

  constructor() {
    // Initialize with stored locale or browser preference
    const stored = localStorage.getItem('locale') as Locale;
    const browserLang = navigator.language.split('-')[0] as Locale;
    const supportedLocales = this.getSupportedLocales();
    
    if (stored && supportedLocales.includes(stored)) {
      this.currentLocale = stored;
    } else if (supportedLocales.includes(browserLang)) {
      this.currentLocale = browserLang;
    }
  }

  setLocale(locale: Locale) {
    this.currentLocale = locale;
    localStorage.setItem('locale', locale);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = locale;
  }

  getLocale(): Locale {
    return this.currentLocale;
  }

  t(key: string, params?: Record<string, string>): string {
    const keys = key.split('.');
    let value: any = translations[this.currentLocale];

    for (const k of keys) {
      value = value?.[k];
      if (!value) break;
    }

    if (typeof value !== 'string') {
      return key; // Return key if translation not found
    }

    // Replace parameters
    if (params) {
      return Object.entries(params).reduce(
        (str, [key, val]) => str.replace(`{${key}}`, val),
        value
      );
    }

    return value;
  }

  getSupportedLocales(): Locale[] {
    return Object.keys(translations) as Locale[];
  }
}

export const i18n = new I18n();
export type { Locale };
